import { defineStore, Store } from "pinia";
import axios from "@/tools/Axios/axios";
import { IUser } from "@/interfaces/User";
import { Organisation } from "@/interfaces/Organisation";
import { toast } from 'vue3-toastify';
import { getAllItemsAction, getItemAction } from "./common/actions";
import { APIListResponse, DefaultGetParams, GetParams } from "@/interfaces/Types.definition";
import { SellingSession } from "@/interfaces/SellingSession";
import { Event } from "@/interfaces/Event";
import * as Sentry from "@sentry/browser";

export interface UserState {
  me: IUser | null;
  sellingSession: SellingSession | null;
  sellingSessions: SellingSession[] | null,
  sessionStarted: boolean;
  accessToken: string | null;
  roles: string[] | null;
  organisation: Organisation | null;
  organisations: Organisation[] | null;
}

export const useUserStore = defineStore("UserStore", {
  state: (): UserState => {
    return {
      me: null,
      sellingSession: null,
      sellingSessions: null,
      sessionStarted: false,
      accessToken: null,
      roles: null,
      organisation: null,
      organisations: null,
    };
  },
  actions: {
    async setOrganisation(organisation: Organisation) {
      const { data } = await axios.get('/auth/' + organisation._id);
      this.accessToken = data.token;
      this.roles = data.roles;
      await storageSet('access_token', data.token)
      this.organisation = organisation
      await this.getSellingSessions()
      Sentry.setContext('organisation', {
        id: organisation._id,
        name: organisation.name
      })
    },
    async logOut() {
      storageRemove('access_token')
      this.$reset()
      toast('Vous avez bien été déconnecté')
    },
    async getMe() {
        let params: GetParams = {
          populate: ['avatarId']
        }
        const { data } = await axios.get("/users/profile/me", { params });
        this.me = data
        Sentry.setContext('user', {
          id: data.id,
          name: `${this.me?.firstName} ${this.me?.lastName}`,
          email: this.me?.email
        })
        await this.getSellingSessions()
        if (!this.sellingSession) {
            await this.getUserOrganisations()
        }
        else {
          if (!this.organisation) {
            this.fetchOrganisation(this.sellingSession.organisationId)
          }
        }
    },
    async createSellingSession(event: Event) {
      await axios.post("/ticket-offices/selling-sessions", {eventId: event._id})
      this.sessionStarted = true
      await this.getSellingSessions()
      if (this.sellingSessions) {
        const currentSession = this.sellingSessions.find((sellingSession: SellingSession) => sellingSession.eventId._id === event._id)
        if (currentSession) {
          this.sellingSession = currentSession
        }
        else {
          this.sellingSession = null
        }
      }
    },
    /**
     * Clôture une seule session de vente. Helper interne utilisé par
     * closeSession (single) et closeSessions (bulk).
     * Retourne true si la clôture API a réussi (PDF/email/print sont best-effort).
     */
    async _closeOneSession(
      session: SellingSession,
      downloadPdf: boolean,
      emailTo: string | null,
      printThermal: boolean,
    ): Promise<boolean> {
      try {
        const response = await axios.post(
          `/ticket-offices/selling-sessions/${session._id}/cash-out`,
          emailTo ? { email: emailTo } : {},
          {
            responseType: downloadPdf ? 'blob' : 'json',
          },
        )

        // Download PDF if requested
        if (downloadPdf && response.data) {
          const { saveAs } = await import('file-saver')
          const sessionNumber = session.sessionNumber || 'session'
          const date = new Date().toISOString().split('T')[0]
          const filename = `sortie-caisse-${sessionNumber}-${date}.pdf`
          saveAs(response.data, filename)
        }

        // Print thermal receipt if requested
        if (printThermal) {
          const { useSellingSessionStore } = await import('./SellingSessionStore')
          const { usePrintStore } = await import('./PrintStore')

          const sellingSessionStore = useSellingSessionStore()
          const printStore = usePrintStore()

          await sellingSessionStore.fetchStats(session._id)
          const printData = sellingSessionStore.prepareCashOutPrintData(session)
          await printStore.printCashOut(printData)
        }

        return true
      } catch (error) {
        Sentry.captureException(error)
        const eventName = session.eventId?.name || session.sessionNumber || 'session'
        toast.error(`Erreur clôture "${eventName}": ${error.message}`)
        return false
      }
    },
    async closeSession(downloadPdf: boolean = false, emailTo: string | null = null, printThermal: boolean = false): Promise<boolean> {
      // Conservé pour compatibilité (un seul appel sur this.sellingSession).
      // Le flow multi-sessions passe par closeSessions().
      try {
        if (this.sellingSession?._id) {
          const ok = await this._closeOneSession(
            this.sellingSession,
            downloadPdf,
            emailTo,
            printThermal,
          )
          if (!ok) return false

          if (emailTo) {
            toast.success(`Sortie de caisse envoyée à ${emailTo}`)
          }
          if (downloadPdf) {
            toast.success('Sortie de caisse téléchargée')
          }
        }
        this.logOut()
        return true
      } catch (error) {
        Sentry.captureException(error)
        toast.error(error.message)
      }
      return false
    },
    /**
     * Clôture plusieurs sessions en série (1 appel API par session).
     * - Continue malgré les erreurs (les autres sessions sont quand même fermées).
     * - logout = true → déconnexion à la fin (comportement par défaut).
     *
     * Retourne le compte de succès/échecs pour permettre à l'UI d'afficher
     * un récap.
     */
    async closeSessions(
      sessionIds: string[],
      options: {
        downloadPdf: boolean
        emailTo: string | null
        printThermal: boolean
        logout: boolean
      },
    ): Promise<{ success: number; failed: number; total: number }> {
      const sessions = (this.sellingSessions || []).filter((s) =>
        sessionIds.includes(s._id),
      )
      let success = 0
      let failed = 0

      for (const session of sessions) {
        const ok = await this._closeOneSession(
          session,
          options.downloadPdf,
          options.emailTo,
          options.printThermal,
        )
        if (ok) success++
        else failed++
      }

      // Toasts globaux (les erreurs individuelles ont déjà été remontées
      // via toast par _closeOneSession).
      if (success > 0) {
        if (options.emailTo) {
          toast.success(
            success === 1
              ? `Sortie de caisse envoyée à ${options.emailTo}`
              : `${success} sorties de caisse envoyées à ${options.emailTo}`,
          )
        }
        if (options.downloadPdf) {
          toast.success(
            success === 1
              ? 'Sortie de caisse téléchargée'
              : `${success} sorties de caisse téléchargées`,
          )
        }
      }

      if (options.logout) {
        this.logOut()
      } else {
        // Pas de logout : on rafraîchit la liste des sessions ouvertes
        // pour que l'UI reflète l'état après clôture.
        await this.getSellingSessions()
        // Si la session courante (celle de l'event ouvert) a été fermée,
        // on la nulle dans le store pour que l'UI / les guards ne la
        // considèrent plus comme active.
        if (
          this.sellingSession?._id &&
          sessionIds.includes(this.sellingSession._id)
        ) {
          this.sellingSession = null
        }
      }

      return { success, failed, total: sessions.length }
    },
    async getSellingSessions() {
      let params: GetParams = DefaultGetParams()
        params.filter = {
          authorId: this.me?._id,
          status: 'opened,paused',
        }
        // Populate eventId + organisationId pour afficher le nom de
        // l'événement et de l'organisation dans la modale de clôture
        // (mode multi-sessions). Sans populate, ces champs sont juste
        // des ID string.
        params.populate = ['eventId', 'organisationId']
        if (this.me) {
          await getAllItemsAction<SellingSession, Store<'UserStore'>>('/ticket-offices/selling-sessions/list', params, this, 'sellingSessions')
        }
    },
    async getAccessToken(email: string, password: string, captchaToken: string): Promise<boolean> {
      try {
        const { data } = await axios.post("/auth", {
          email,
          password,
          captchaProvider: 'turnstile',
          captchaToken,
        });
        this.accessToken = data.token;
        this.roles = data.roles;
        await storageSet('access_token', data.token)
        await this.getMe()
        return true
      } catch (error) {
        if (error.constructor.name == 'AxiosError') {
          if (error.response.status == 401) {
            toast.error('Identifiants invalides')
            return false
          }
          Sentry.captureException(error)
          return false
        }
        else {
          Sentry.captureException(error)
        }
        toast.error(error.message)
        return false
      }
    },

    async getUserOrganisations() {
        let params: GetParams = DefaultGetParams()
        params.populate = ['imageId', "seatsIoWorkspaceSecret"]
        await getAllItemsAction<Organisation, Store<'UserStore'>>('/users/organisations/list', params, this, 'organisations')
    },

    async fetchOrganisation(organisationId: string) {
      let params: GetParams = DefaultGetParams()
      params.populate = ["seatsIoWorkspaceSecret"]
      await getItemAction<Organisation, Store<'UserStore'>>(`/users/organisations/${organisationId}`, params, this, 'organisation')
      Sentry.setContext('organisation', {
        id: this.organisation?.id,
        name: this.organisation?.name
      })
    },
    async userGuard() {
        if (!this.me) {
            const accessToken = await storageGet('access_token');
            if (accessToken) {
                this.accessToken = accessToken
                try {
                    await this.getMe();
                    return true;
                } catch (e) {
                    this.accessToken = null
                    await storageRemove('access_token');
                    toast.error('Connexion impossible');
                    return false;
                }
            } else {
                return false;
            }
        } else {
            return true;
        }
    },
  },
});

export const storageSet = async (key: string, value: any) => {
  localStorage.setItem(key, value)
};

export const storageGet = async (key: string) => {
    return localStorage.getItem(key)
};

export const storageRemove = async (key: string) => {
    localStorage.removeItem(key)
};
